import {
  Box,
  Flex,
  Heading,
  Icon,
  Spinner,
  Text,
  Tooltip,
  useToast,
  keyframes,
  Link,
  Img,
  background,
  Table,
  TableContainer,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Progress
} from '@chakra-ui/react';
import Skeleton from 'react-loading-skeleton';
import { useMatomo } from '@jonkoops/matomo-tracker-react';
import { useAppContext, usePDFReportContext, useAuthContext } from 'Context';
import useDocumentTitle from 'Helpers/useDocumentTitle';
import React, { useEffect, useState } from 'react';
import {
  calculateCentroid,
  formatToServerFormat,
  getRandomHexColor,
  graphColors,
  kFormatter
} from 'Helpers/helpers';
import { FieldForecast, Field as FieldD, Field, User, ProductionCategory } from 'Context/types';
import { PDFDownloadLink } from '@react-pdf/renderer';
import WeeklyPDFReport from 'Pages/PDFReporting/Components/WeeklyPDFReport';
import moment from 'moment';
import useDelayedLoadingState from 'Helpers/useDelayedLoadingState';
import { Download } from 'react-feather';
import { isArray } from 'lodash';

import LogoWithSlogan from '../../../assets/images/LogoWithSlogan.png';
import PDFIconHome from '../Assets/PDFIconHome.png';
import PDFIconMenu from '../Assets/PDFIconMenu.png';
import PDFIconStems from '../Assets/PDFIconStems.png';
import PDFIconPinOutline from '../Assets/PDFIconPinOutline.png';

import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ErrorBar,
  Label,
  LabelList,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  XAxis,
  YAxis
} from 'recharts';
import { useLocation } from 'react-router-dom';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';

import '../Assets/live_report.scss';
import { use } from 'chai';

const LiveReporting = () => {
  // const { trackPageBox } = useMatomo();
  const {
    functions: { getToken, setUser, setUserCookie },
    variables: { user }
  } = useAuthContext();
  const { farm, varieties, variety } = useAppContext();
  const {
    x_api_key,
    setXApiKey,
    getFarms,
    fields,
    fetchFieldsData,
    getVarietyForecastData,
    getVarietyHarvestData,
    getVarietyMilestoneForecastData,
    loadingFields,
    loadingForecasts,
    loadingWeather,
    fetchWeatherData,
    weatherData,
    farmSections,
    fetchFarmSections
  } = usePDFReportContext();
  const toast = useToast();
  // Track page view
  useDocumentTitle('Live Tv Reporting | Lima Asili');

  // useEffect(() => {
  //   trackPageBox();
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  const [reportWeekNo, setReportWeekNo] = useState(moment().add('week', 1).isoWeek());
  const [dateString, setDateString] = useState(
    moment().add('week', 1).startOf('isoWeek').format('DD-MMM-YYYY')
  );
  const [varietyForecasts, setVarietyForecasts] = useState<any[]>([]);
  const [activeVarietyIndex, setActiveVarietyIndex] = useState<number>(-1);
  const [activePages, setActivePages] = useState<
    { name: string; display_time: number; varietyPageIndex?: number; sectionId?: number }[]
  >([{ name: 'Farm Summary', display_time: 15000 }]);
  const [activePageIndex, setActivePageIndex] = useState<number>(0);
  const [varietyGridConfig, setVarietyGridConfig] = useState<any>('46%'); // Default width for variety graphs
  const [varietyHarvests, setVarietyHarvests] = useState<any[]>([]);
  // const [varietyMilestoneForecasts, setVarietyMilestoneForecasts] = useState<any[]>([]);
  const [totalFarmForecast, setTotalFarmForecast] = useState<number>(0);
  const [totalFarmHarvest, setTotalFarmHarvest] = useState<number>(0);
  const [varietyGraphData, setVarietyGraphData] = useState<any[]>([]);
  const [individualVarietyGraphData, setIndividualVarietyGraphData] = useState<any[]>([]);
  const [fieldForecasts, setFieldForecasts] = useState<Record<string, FieldForecast>>({});
  const [weatherGraphData, setWeatherGraphData] = useState<any[]>([]);
  const [weatherWeekOverview, setWeatherWeekOverview] = useState<any[]>([]);
  const [fieldsWithData, setFieldsWithData] = useState<FieldD[]>([]);
  const [allFarmSectionData, setAllFarmSectionData] = useState<
    Array<{
      section: any;
      totalForecast: number;
      totalHarvest: number;
      activeFieldsCount: number;
      totalFieldsCount: number;
      graphData: any[];
    }>
  >([]);
  const [hidePdf, setHidePdf] = useState<boolean>(true);
  const isProcessing = useDelayedLoadingState({
    loadingState: loadingFields || loadingForecasts || loadingWeather,
    delayMs: 3000
  });

  const next_no_of_weeks = 2;
  const past_no_of_weeks = 2;

  const [lastUpdated, setLastUpdated] = useState<string>('');

  const [weatherDays, setWeatherDays] = useState<moment.Moment[]>([]);
  const [isGeneratingReport, setIsGeneratingReport] = useState<boolean>(false);
  const [progressPercentage, setProgressPercentage] = useState<number>(0);
  const [generationStartTime, setGenerationStartTime] = useState<number>(0);
  const [generationTime, setGenerationTime] = useState<string>('');
  const [batchProgress, setBatchProgress] = useState<string>('');

  // Helper function to batch promises in chunks to avoid overwhelming the server
  const batchPromises = async <T,>(
    promises: (() => Promise<T>)[],
    // eslint-disable-next-line @typescript-eslint/no-inferrable-types
    batchSize: number = 15,
    onBatchComplete?: (completed: number, total: number) => void
  ): Promise<T[]> => {
    const results: T[] = [];
    for (let i = 0; i < promises.length; i += batchSize) {
      const batch = promises.slice(i, i + batchSize);
      const batchResults = await Promise.all(batch.map((fn) => fn()));
      results.push(...batchResults);
      if (onBatchComplete) {
        onBatchComplete(Math.min(i + batchSize, promises.length), promises.length);
      }
    }
    return results;
  };

  // useEffect to change active varitey index every 20 seconds
  useEffect(() => {
    if (varieties.length <= 1) return;
    const interval = setInterval(() => {
      setActiveVarietyIndex((prevIndex) => (prevIndex + 1) % 2);

      // setActiveVarietyIndex((prevIndex) => (prevIndex + 1) % varieties.length);

      // useEffect to change active page every 20 seconds
    }, 20000); // Change every 20 seconds

    return () => clearInterval(interval);
  }, [varieties.length]);

  // useEffect to change active page based on each page's display_time
  useEffect(() => {
    if (varieties.length <= 1 || activePages.length === 0) return;

    const currentPage = activePages[activePageIndex];
    const displayTime = currentPage?.display_time || 20000;

    const timeout = setTimeout(() => {
      setActivePageIndex((prevIndex) => (prevIndex + 1) % activePages.length);
    }, displayTime);

    return () => clearTimeout(timeout);
  }, [activePageIndex, activePages.length, varieties.length]);

  // Keyboard listener for Enter key to go to next page
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        setActivePageIndex((prevIndex) => (prevIndex + 1) % activePages.length);
      }
    };

    window.addEventListener('keydown', handleKeyPress);

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [activePages.length]);

  // Fetch farm sections when farm or x_api_key changes
  useEffect(() => {
    if (farm && x_api_key) {
      fetchFarmSections(farm, x_api_key);
    }
  }, [farm, x_api_key, fetchFarmSections]);

  const fetchFarmForecasts = async (report_date: Date) => {
    const startTime = Date.now();
    setGenerationStartTime(startTime);
    setIsGeneratingReport(true);
    setProgressPercentage(0);
    setGenerationTime('');

    // set lastUpdated
    setLastUpdated(moment(report_date).format('DD-MMM-YYYY hh:mm A'));

    setVarietyForecasts([]);
    setVarietyHarvests([]);
    // setVarietyMilestoneForecasts([]);
    setTotalFarmForecast(0);
    setTotalFarmHarvest(0);
    // set start and end date for next 4 weeks
    const start_date = formatToServerFormat(
      moment(report_date).subtract(past_no_of_weeks, 'weeks').startOf('isoWeek').toDate()
    );
    const end_date = formatToServerFormat(
      moment(report_date).add('weeks', next_no_of_weeks).endOf('isoWeek').startOf('day').toDate()
    );
    const variety_forecasts: any[] = [];
    const variety_harvests: any[] = [];
    const variety_milestones: any[] = [];
    let total_forecasts = 0;
    let total_harvests = 0;

    // Calculate total steps for progress tracking
    const totalSteps = 4; // forecast + harvest + processing + preparation
    let currentStep = 0;

    // Update progress helper
    const updateProgress = () => {
      currentStep++;
      setProgressPercentage((currentStep / totalSteps) * 100);
    };

    setProgressPercentage(5); // Starting fetches

    const useBatching = varieties.length > 20; // Batch if more than 20 varieties
    const batchSize = 15; // Process 15 varieties at a time

    let forecastResults: any[];
    let harvestResults: any[];

    if (useBatching) {
      // Fetch forecasts in batches
      forecastResults = await batchPromises(
        varieties.map(
          (v) => () =>
            getVarietyForecastData(x_api_key, v.id, start_date, end_date, null).catch((error) => {
              console.error(`Error fetching forecast for variety ${v.id}:`, error);
              return null;
            })
        ),
        batchSize,
        (completed, total) => {
          setBatchProgress(`Forecasts: ${completed}/${total}`);
        }
      );
      updateProgress();
      setBatchProgress('');

      // Fetch harvests in batches
      harvestResults = await batchPromises(
        varieties.map(
          (v) => () =>
            getVarietyHarvestData(x_api_key, v.id, start_date, end_date, null).catch((error) => {
              console.error(`Error fetching harvest for variety ${v.id}:`, error);
              return null;
            })
        ),
        batchSize,
        (completed, total) => {
          setBatchProgress(`Harvests: ${completed}/${total}`);
        }
      );
      updateProgress();
      setBatchProgress('');
    } else {
      // For smaller farms, fetch all at once
      const forecastPromises = varieties.map((v) =>
        getVarietyForecastData(x_api_key, v.id, start_date, end_date, null).catch((error) => {
          console.error(`Error fetching forecast for variety ${v.id}:`, error);
          return null;
        })
      );
      const harvestPromises = varieties.map((v) =>
        getVarietyHarvestData(x_api_key, v.id, start_date, end_date, null).catch((error) => {
          console.error(`Error fetching harvest for variety ${v.id}:`, error);
          return null;
        })
      );

      [forecastResults, harvestResults] = await Promise.all([
        Promise.all(forecastPromises).then((results) => {
          updateProgress();
          return results;
        }),
        Promise.all(harvestPromises).then((results) => {
          updateProgress();
          return results;
        })
      ]);
    }

    // Process results
    for (let index = 0; index < varieties.length; index++) {
      const variety_forecast = forecastResults[index] as any;
      if (variety_forecast) {
        variety_forecasts.push(variety_forecast);
        total_forecasts += variety_forecast.forecasts[past_no_of_weeks];
      }

      const variety_harvest = harvestResults[index] as any;
      if (variety_harvest) {
        variety_harvests.push(variety_harvest);
        total_harvests += variety_harvest.harvests[past_no_of_weeks];
      }
    }

    setVarietyForecasts(variety_forecasts);
    setVarietyHarvests(variety_harvests);
    // setVarietyMilestoneForecasts(variety_milestones);
    setTotalFarmForecast(total_forecasts);
    setTotalFarmHarvest(total_harvests);

    // Update progress for data processing
    updateProgress();

    // initializ active variety in case there are multiple varieties
    if (variety_forecasts.length > 1) {
      setActiveVarietyIndex(0);
    }

    // Progress will be completed after graph preparation
    setProgressPercentage(95);
  };

  useEffect(() => {
    const varieties_count = varieties.length;
    const varietyGridConfig: any = {
      items_per_page: 0,
      items_per_row: 0,
      item_width: '46%',
      item_height: '46%',
      number_of_pages: 1
    };
    // calculate variety page config based on number of varieties
    // Note: We use a 2% gap between items, so we need to account for that in width calculations
    // Formula: item_width = (100 - (gap * (items_per_row - 1))) / items_per_row
    const gap = 2; // 2% gap between items

    if (varieties_count % 5 === 0 || varieties_count > 15) {
      varietyGridConfig.items_per_row = 5;
      varietyGridConfig.item_width = ((100 - gap * (5 - 1)) / 5).toFixed(2) + '%'; // ~18.4%
    } else if (varieties_count % 4 === 0) {
      varietyGridConfig.items_per_row = 4;
      varietyGridConfig.item_width = ((100 - gap * (4 - 1)) / 4).toFixed(2) + '%'; // ~23.5%
    } else if (varieties_count % 3 === 0) {
      varietyGridConfig.items_per_row = 3;
      varietyGridConfig.item_width = ((100 - gap * (3 - 1)) / 3).toFixed(2) + '%'; // ~32%
    } else if (varieties_count % 2 === 0) {
      varietyGridConfig.items_per_row = 2;
      varietyGridConfig.item_width = ((100 - gap * (2 - 1)) / 2).toFixed(2) + '%'; // ~49%
    } else {
      // Odd numbers not divisible by 3, 4, or 5
      varietyGridConfig.items_per_row = Math.min(4, varieties_count);
      varietyGridConfig.item_width =
        (
          (100 - gap * (varietyGridConfig.items_per_row - 1)) /
          varietyGridConfig.items_per_row
        ).toFixed(2) + '%';
    }

    // Calculate items per page and number of pages (max 15 items per page)
    varietyGridConfig.items_per_page = Math.min(15, varieties_count);
    varietyGridConfig.number_of_pages = Math.ceil(varieties_count / 15);
    varietyGridConfig.rows_per_page = Math.ceil(
      varietyGridConfig.items_per_page / varietyGridConfig.items_per_row
    );
    varietyGridConfig.item_height = 93 / varietyGridConfig.rows_per_page + '%';
    setVarietyGridConfig(varietyGridConfig);

    // Update activePages based on number of varieties
    const newActivePages: {
      name: string;
      display_time: number;
      varietyPageIndex?: number;
      sectionId?: number;
    }[] = [{ name: 'Farm Summary', display_time: 15000 }];

    // Add farm section pages - one for each farm section
    if (farmSections && farmSections.length > 0) {
      farmSections.forEach((section: any) => {
        newActivePages.push({
          name: 'Farm Section Summary',
          display_time: 15000,
          sectionId: section.id
        });
      });
    }

    // Add variety pages - split into multiple pages if more than 15 varieties
    const numberOfVarietyPages = Math.ceil(varieties_count / 15);
    for (let i = 0; i < numberOfVarietyPages; i++) {
      newActivePages.push({
        name: 'Multiple-Varieties',
        display_time: 40000,
        varietyPageIndex: i
      });
    }

    setActivePages(newActivePages);
  }, [varieties.length, farmSections]);

  // useEffect(() => {
  //   console.log('varietyGraphWidth', varietyGridConfig, varieties.length);
  // }, [varietyGridConfig]);

  const prepareVarietyGraphData = () => {
    const graph_data: any[] = [];
    const individual_variety_graph_data: any[] = [];

    for (let week_index = 0; week_index <= next_no_of_weeks + past_no_of_weeks; week_index++) {
      const graph_data_week_item: any = {
        name:
          'Week ' +
          moment(dateString, 'DD-MMM-YYYY')
            .add('week', week_index - past_no_of_weeks)
            .isoWeek(),
        Forecasts: 0, // total forecasts for the week
        Actuals: 0, // total harvests for the week
        forecasted_fields: [] // names of the fields that have been forecasted in that week
      };
      varietyForecasts.forEach((varietyForecast, varietyIndex) => {
        // create an object for the variety forecast of a single variety
        const graph_data_single_variety_week_item: any = {
          name:
            'Week ' +
            moment(dateString, 'DD-MMM-YYYY')
              .add('week', week_index - past_no_of_weeks)
              .isoWeek(),
          Forecasts: varietyForecast.forecasts[week_index], // total forecasts for the week
          Actuals: varietyHarvests[varietyIndex]?.harvests?.[week_index] || 0 // total harvests for the week
        };
        if (!individual_variety_graph_data[varietyIndex]) {
          individual_variety_graph_data[varietyIndex] = [];
        }
        individual_variety_graph_data[varietyIndex][week_index] = {
          ...graph_data_single_variety_week_item,
          name: graph_data_single_variety_week_item.name.replace('Week ', 'Wk ')
        };

        //sum total forecasts and total harvests for the week
        graph_data_week_item['Forecasts'] += varietyForecast.forecasts[week_index];
        if (varietyHarvests[varietyIndex]) {
          graph_data_week_item['Actuals'] += varietyHarvests[varietyIndex].harvests[week_index];
        }
        const forecasted_fields: any[] = Object.values(
          varietyForecast.field_forecasts.filter((field_forecast: any) => {
            const field_forecast_object: any = Object.values(field_forecast)[0];
            return field_forecast_object.forecasts[week_index] > 0;
          })
        );
        // get field names from forecasted fields
        graph_data_week_item['forecasted_fields'] = forecasted_fields.map(
          (field: any) => Object.keys(field)[0]
        );

        graph_data_week_item[varietyForecast.variety_name.replace(/\s/g, '')] =
          varietyForecast.forecasts[week_index];

        // // prepare error bar data
        // if (varietyMilestoneForecasts[varietyIndex]) {
        //   graph_data_week_item['forecastErrorBar'] = [
        //     varietyMilestoneForecasts[varietyIndex].forecasts[week_index].weeks_out_2 -
        //       varietyForecast.forecasts[week_index],
        //     varietyMilestoneForecasts[varietyIndex].forecasts[week_index].weeks_out_1 -
        //       varietyForecast.forecasts[week_index]
        //   ].sort((a, b) => a - b);
        // }
      });
      graph_data.push(graph_data_week_item);
    }

    setVarietyGraphData(graph_data);
    setIndividualVarietyGraphData(individual_variety_graph_data);
  };

  // New function to prepare farm section data
  const prepareFarmSectionData = () => {
    if (!farmSections || farmSections.length === 0) return;

    const sectionDataArray: Array<{
      section: any;
      totalForecast: number;
      totalHarvest: number;
      activeFieldsCount: number;
      totalFieldsCount: number;
      graphData: any[];
    }> = [];

    farmSections.forEach((section: any) => {
      // Filter fields for this section
      const sectionFields = fieldsWithData.filter((field) => field.farm_section_id === section.id);

      // Calculate totals
      let totalForecast = 0;
      let totalHarvest = 0;

      // Prepare graph data for this section
      const sectionGraphData: any[] = [];

      for (let week_index = 0; week_index <= next_no_of_weeks + past_no_of_weeks; week_index++) {
        const graph_data_week_item: any = {
          name:
            'Week ' +
            moment(dateString, 'DD-MMM-YYYY')
              .add('week', week_index - past_no_of_weeks)
              .isoWeek(),
          Forecasts: 0,
          Actuals: 0
        };

        // Sum up forecasts and harvests for all fields in this section
        varietyForecasts.forEach((varietyForecast) => {
          // Get fields for this variety in this section
          const varietyFieldsInSection = sectionFields.filter(
            (field) => field.variety_id === varietyForecast.query_metadata.variety_id
          );

          varietyFieldsInSection.forEach((field) => {
            // Find field forecast data - use field name + variety_id as key
            const fieldForecastKey = field.name + '_' + field.variety_id;
            const fieldForecastData = fieldForecasts[fieldForecastKey];

            if (fieldForecastData) {
              const forecastValue = fieldForecastData.forecasts[week_index] || 0;
              const harvestValue = fieldForecastData.harvests?.[week_index] || 0;

              graph_data_week_item.Forecasts += forecastValue;
              graph_data_week_item.Actuals += harvestValue;

              // For current week (past_no_of_weeks), add to totals
              if (week_index === past_no_of_weeks) {
                totalForecast += forecastValue;
                totalHarvest += harvestValue;
              }
            }
          });
        });

        sectionGraphData.push(graph_data_week_item);
      }

      const sectionData = {
        section,
        totalForecast,
        totalHarvest,
        activeFieldsCount: sectionFields.length,
        totalFieldsCount: fields.filter((f) => f.farm_section_id === section.id).length,
        graphData: sectionGraphData
      };

      sectionDataArray.push(sectionData);
    });

    setAllFarmSectionData(sectionDataArray);
  };

  useEffect(() => {
    prepareVarietyGraphData();
    prepareFarmSectionData();

    // Complete the progress when graph data is ready
    if (varietyForecasts.length > 0 && isGeneratingReport) {
      setProgressPercentage(100);
      // Calculate generation time
      const endTime = Date.now();
      const durationMs = endTime - generationStartTime;
      const minutes = Math.floor(durationMs / 60000);
      const seconds = Math.floor((durationMs % 60000) / 1000);
      setGenerationTime(minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`);

      // Small delay to show 100% before hiding loading
      setTimeout(() => {
        setIsGeneratingReport(false);
      }, 300);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [varietyForecasts, varietyHarvests, fieldForecasts, farmSections]);
  useEffect(() => {
    const field_forecasts_array: Record<string, FieldForecast> = fields.reduce((acc, field) => {
      acc[field.name + '_' + field.variety_id] = {
        forecasts: Array(past_no_of_weeks + next_no_of_weeks + 1).fill(0),
        // harvests: null,
        graph_data: [],
        detailed_view: []
      };
      return acc;
    }, {} as Record<string, FieldForecast>);
    varietyForecasts.forEach((varietyForecast) => {
      const varietyHarvest = varietyHarvests.filter(
        (varietyHarvest) =>
          varietyHarvest.query_metadata.variety_id === varietyForecast.query_metadata.variety_id
      )[0];
      varietyForecast.field_forecasts.forEach((field_forecasts: any) => {
        const field_forecast_object: FieldForecast = Object.values(
          field_forecasts
        )[0] as FieldForecast;

        // add field harvests for varietyHarvest to field forecast object
        const field_harvests = varietyHarvest?.field_harvests?.find(
          (field_harvests: any) =>
            Object.keys(field_harvests)[0] === Object.keys(field_forecasts)[0]
        );

        //prepare field graph data
        const field_graph_data: any[] = [];
        for (let week_index = 0; week_index <= next_no_of_weeks + past_no_of_weeks; week_index++) {
          const graph_data_week_item: any = {
            name: 'Week ' + (reportWeekNo + week_index - past_no_of_weeks)
          };

          Object.keys(varietyForecast?.production_category_ids).forEach(
            (production_category_id: any) => {
              graph_data_week_item[
                varietyForecast?.production_category_ids[production_category_id].replace(/\s/g, '')
              ] = field_forecast_object?.detailed_view
                ? field_forecast_object?.detailed_view[week_index][production_category_id] ?? 2
                : 1;
            }
          );
          field_graph_data.push(graph_data_week_item);
        }

        // create field_forecast_object
        field_forecasts_array[
          Object.keys(field_forecasts)[0] + '_' + varietyForecast.query_metadata.variety_id
        ] = {
          ...(Object.values(field_forecasts)[0] as FieldForecast),
          harvests: field_harvests
            ? field_harvests[Object.keys(field_harvests)[0]].harvests
            : undefined,
          graph_data: field_graph_data
        };
      });
    });

    setFieldForecasts(field_forecasts_array);

    // remove fields from the fields array that dont have forecasts or harvests in the field_forecasts_array
    const fields_with_data = fields.filter((field) => {
      const fieldKey = field.name + '_' + field.variety_id;
      if (
        field_forecasts_array[fieldKey].forecasts.reduce((acc, forecast) => acc + forecast, 0) ===
          0 ||
        field_forecasts_array[fieldKey].harvests === null
      )
        return false;
      return field_forecasts_array[fieldKey];
    });
    setFieldsWithData(fields_with_data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    setTimeout(() => {
      setHidePdf(false);
    }, 10000);
  }, [fields, varietyForecasts.length, varietyHarvests]);

  useEffect(() => {
    // clear toast when component unmounts
    return () => {
      toast.closeAll();
    };
  });

  useEffect(() => {
    // prepare graph data for weather
    const weather_graph_data: any[] = [];
    weatherDays.forEach((weatherDay: moment.Moment, day_index: number) => {
      const graph_data_item: any = {
        name: weatherDay.format('ddd DD')
      };

      if (weatherData?.weather && weatherData?.weather[day_index]) {
        graph_data_item['Min'] = weatherData.weather[day_index]?.mintempC;
        graph_data_item['Max'] = weatherData.weather[day_index].maxtempC;
        graph_data_item['rainfall'] = weatherData.weather[day_index].hourly[0].precipMM;
      }
      weather_graph_data.push(graph_data_item);
    });
    setWeatherGraphData(weather_graph_data);

    if (isArray(weatherData?.weather)) {
      prepareWeeklyWeatherOverview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weatherData, weatherDays]);

  const prepareWeeklyWeatherOverview = () => {
    const today = moment();
    // Initialize variables to track metrics
    const week_overview: {
      dates: string;
      maxTempC: { avg: number; max: number; min: number; total: number; count: number };
      minTempC: { avg: number; max: number; min: number; total: number; count: number };
      precipMM: { avg: number; max: number; min: number; total: number; count: number };
      humidity: { avg: number; max: number; min: number; total: number; count: number };
    }[] = [
      {
        dates: `${today.startOf('isoWeek').add(1, 'week').format('DD/MM')} - ${today
          .endOf('isoWeek')
          .format('DD/MM')}`,
        maxTempC: {
          avg: 0,
          max: 0,
          min: parseFloat(weatherData.weather[0]['maxtempC']),
          total: 0,
          count: 0
        },
        minTempC: {
          avg: 0,
          max: 0,
          min: +weatherData.weather[0]['mintempC'],
          total: 0,
          count: 0
        },
        precipMM: {
          avg: 0,
          max: 0,
          min: +weatherData.weather[0].hourly[0].precipMM,
          total: 0,
          count: 0
        },
        humidity: {
          avg: 0,
          max: 0,
          min: +weatherData.weather[0].hourly[0].humidity,
          total: 0,
          count: 0
        }
      },
      {
        dates: `${today.add(1, 'week').startOf('isoWeek').format('DD/MM')} - ${today
          .endOf('isoWeek')
          .format('DD/MM')}`,
        maxTempC: {
          avg: 0,
          max: 0,
          min: parseFloat(weatherData.weather[7]['maxtempC']),
          total: 0,
          count: 0
        },
        minTempC: {
          avg: 0,
          max: 0,
          min: parseFloat(weatherData.weather[7]['mintempC']),
          total: 0,
          count: 0
        },
        precipMM: {
          avg: 0,
          max: 0,
          min: +weatherData.weather[7]?.hourly[0].precipMM,
          total: 0,
          count: 0
        },
        humidity: {
          avg: 0,
          max: 0,
          min: +weatherData.weather[7]?.hourly[0].humidity,
          total: 0,
          count: 0
        }
      }
    ];

    // Loop through each day in the weather data
    // weatherData.weather.forEach((day: any) => {
    for (let index = 0; index < weatherData?.weather.length; index++) {
      let week_index = 0;
      if (index > 6) {
        week_index = 1;
      }
      // Process daily values for maxTempC, minTempC, and humidity
      const maxtempC = parseFloat(weatherData.weather[index]['maxtempC']);
      const mintempC = parseFloat(weatherData.weather[index]['mintempC']);
      const humidity = parseFloat(weatherData.weather[index].hourly[0].humidity);
      const precipMM = parseFloat(weatherData.weather[index].hourly[0].precipMM);

      // Update max/min/average values for daily maxTempC, minTempC, and humidity
      week_overview[week_index].maxTempC.total += maxtempC;
      week_overview[week_index].maxTempC.count += 1;
      week_overview[week_index].maxTempC.max = Math.max(
        week_overview[week_index].maxTempC.max,
        maxtempC
      );
      week_overview[week_index].maxTempC.min = Math.min(
        week_overview[week_index].maxTempC.min,
        maxtempC
      );

      week_overview[week_index].minTempC.total += mintempC;
      week_overview[week_index].minTempC.count += 1;
      week_overview[week_index].minTempC.max = Math.max(
        week_overview[week_index].minTempC.max,
        mintempC
      );
      week_overview[week_index].minTempC.min = Math.min(
        week_overview[week_index].minTempC.min,
        mintempC
      );

      week_overview[week_index].humidity.total += humidity;
      week_overview[week_index].humidity.count += 1;
      week_overview[week_index].humidity.max = Math.max(
        week_overview[week_index].humidity.max,
        humidity
      );
      week_overview[week_index].humidity.min = Math.min(
        week_overview[week_index].humidity.min,
        humidity
      );

      week_overview[week_index].precipMM.total += precipMM;
      week_overview[week_index].precipMM.count += 1;
      week_overview[week_index].precipMM.max = Math.max(
        week_overview[week_index].precipMM.max,
        precipMM
      );
      week_overview[week_index].precipMM.min = Math.min(
        week_overview[week_index].precipMM.min,
        precipMM
      );

      // // Process hourly data for precipMM
      // day.hourly.forEach((hour) => {
      //   const precip = parseFloat(hour.precipMM);
      //   precipMM.total += precip;
      //   precipMM.count += 1;
      //   precipMM.max = Math.max(precipMM.max, precip);
      //   precipMM.min = Math.min(precipMM.min, precip);
      // });

      // Calculate averages for each category
      if (index % 7 == 0) {
        week_overview[week_index].maxTempC.avg =
          week_overview[week_index].maxTempC.total / week_overview[week_index].maxTempC.count;

        week_overview[week_index].minTempC.avg =
          week_overview[week_index].minTempC.total / week_overview[week_index].minTempC.count;
        week_overview[week_index].humidity.avg =
          week_overview[week_index].humidity.total / week_overview[week_index].humidity.count;
        week_overview[week_index].precipMM.avg =
          week_overview[week_index].precipMM.total / week_overview[week_index].precipMM.count;
      }
    }

    setWeatherWeekOverview(week_overview);
  };

  const calculateDays = (start_date: moment.Moment) => {
    const today = moment().startOf('day');
    const end_date = start_date.clone().add(14, 'days').endOf('day');
    let future_days = 0;
    let past_start_day = null;
    let past_end_day = null;

    for (let date = start_date.clone(); date.isBefore(end_date); date.add(1, 'day')) {
      if (date.isSameOrAfter(today)) {
        future_days++;
      } else {
        if (!past_start_day) {
          past_start_day = date.clone();
        }
        past_end_day = date.clone();
      }
    }

    return { future_days, past_start_day, past_end_day };
  };

  const generateReport = (values: { report_date: Date | null }, { setSubmitting }: any) => {
    if (farm && varieties.length > 0 && values.report_date) {
      setReportWeekNo(moment(values.report_date).isoWeek());
      setDateString(moment(values.report_date).format('DD-MMM-YYYY'));

      fetchFarmSections(farm, x_api_key);
      fetchFieldsData(x_api_key);
      fetchFarmForecasts(values.report_date);

      const first_day = moment(values.report_date);
      const { future_days, past_start_day, past_end_day } = calculateDays(first_day);
      // Create an array of 7 days starting from the next week's Monday
      const daysArray: moment.Moment[] = [];

      for (let i = 0; i < 7; i++) {
        daysArray.push(first_day.clone().add(i, 'days'));
      }
      setWeatherDays(daysArray);

      fetchWeatherData(
        future_days,
        past_start_day?.format('YYYY-MM-DD'),
        past_end_day?.format('YYYY-MM-DD')
      );

      setHidePdf(false);
    }
    setSubmitting(false);
  };
  // Create styles
  const styles = {
    page: {
      flexDirection: 'row',
      backgroundColor: '#ffffff',
      fontFamily: 'Be Vietnam Pro'
    },
    section: {
      margin: 30,
      padding: 10,
      flexGrow: 1
    },
    section_title: {
      fontSize: '1.3rem',
      marginBottom: 8,
      fontFamily: 'Be Vietnam Pro',
      fontWeight: 700
    },
    green_page_title: {
      fontSize: '1.1rem',
      marginRight: '2vw',
      fontWeight: 700
    },
    text: { margin: 12, fontSize: 14, textAlign: 'justify', fontFamily: 'Times-Roman' },
    info_card: {
      width: '40vw',
      height: '15vh',
      marginTop: '2vh',
      marginLeft: 12,
      borderRadius: 6,
      backgroundColor: '#00A896',
      color: '#000',
      alignItems: 'left'
    },
    info_card_multivar: {
      width: '17vw',
      height: '21vh',
      marginTop: '2vh',
      marginLeft: 12,
      borderRadius: 6,
      backgroundColor: '#00A896',
      color: '#ffffff',
      alignItems: 'center'
    },
    info_card_multivar_alt: {
      width: '17vw',
      height: '21vh',
      marginTop: '2vh',
      marginLeft: 12,
      borderRadius: 6,
      backgroundColor: '#CCEEEA',
      color: '#ffffff',
      alignItems: 'center'
    },
    info_card_bottom: {
      backgroundColor: '#CCEEEA',
      width: '100%',
      height: '70%',
      alignItems: 'center',
      borderRadius: 6
    },
    info_card_top: {
      flex: 'columns',
      width: '100%',
      height: '56%',
      alignItems: 'left',
      borderRadius: 6
    },
    info_card_description: { fontSize: '1.3rem', fontWeight: 700 },
    info_card_value: {
      fontSize: '2.2rem',
      fontWeight: 800,
      marginTop: 16
    },

    info_card_stems_icon: { width: '20px', height: 20, marginTop: 12 },
    info_card_stems_text: { fontSize: 12, color: '#000' },
    cover_page_link: {
      fontFamily: 'Be Vietnam Pro',
      color: '#141414',
      fontSize: 16,
      textDecoration: 'underline',
      marginTop: 6,
      marginBottom: 6
    },
    field_week_item: {
      width: '8vw',
      height: '4vh',
      marginRight: '1vw',
      marginBottom: '1vw',
      paddingTop: '0.2rem',
      paddingBottom: '1vh',
      borderRadius: 6,
      alignItems: 'center',
      color: '#000',
      fontSize: '1.2rem',
      backgroundColor: '#CCCCCC'
    },
    field_grid_item: {
      width: '4vw',
      height: '4vh',
      marginRight: '1vw',
      marginBottom: '1vw',
      paddingTop: '0.2rem',
      paddingBottom: '1vh',
      borderRadius: 6,
      backgroundColor: '#3BF59C',
      alignItems: 'center',
      color: '#000',
      fontSize: '1.1rem'
    },
    field_details: { fontSize: 12, marginBottom: 6 },
    bold_text: {
      fontWeight: 'bold'
    },
    field_weekly_forecast_container: { flexDirection: 'row', height: 34, marginBottom: 4 },
    field_weekly_forecast_left: {
      width: 50,
      backgroundColor: '#CCEEEA',
      color: '#000',
      borderTopLeftRadius: 12,
      borderBottomLeftRadius: 12,
      paddingTop: 7,
      paddingLeft: 10,
      paddingRight: 12
    },
    field_weekly_forecast_center: {
      backgroundColor: '#00A896',
      color: '#fff',
      padding: 8,
      flexDirection: 'row',
      alignItems: 'center',
      width: 95
    },
    field_weekly_forecast_right: {
      backgroundColor: '#00332d',
      color: '#fff',
      borderTopRightRadius: 12,
      borderBottomRightRadius: 12,
      padding: 8,
      flexDirection: 'row',
      alignItems: 'center',
      width: 95
    },
    daily_weather_item: { display: 'flex', flexDirection: 'row', marginLeft: 12, marginBottom: 4 },
    daily_weather_item_icon: { width: 14, marginRight: 4, padding: 2 }
  };

  const map_zoom_level = { field: 18, farm: 15 };

  function useQuery() {
    const { search } = useLocation();
    return React.useMemo(() => new URLSearchParams(search), [search]);
  }

  const query = useQuery();
  const [refreshInterval, setRefreshInterval] = useState<number>(60); // report refresh interval in minutes

  useEffect(() => {
    const user_data: User = {
      username: '',
      full_name: '',
      email: '',
      role: 'Member',
      is_active: true,
      user_group: '',
      tenant_header: atob(query.get('CSAOFS') || ''),
      onboarding_status: 'complete'
    };
    setUser(user_data);
    setUserCookie('user', user_data, {
      path: '/',
      expires: moment()
        .add(60 * 60 * 24 * 30, 's')
        .toDate(),
      secure: true,
      sameSite: 'strict'
    });
    setXApiKey(query.get('base') || '');
    setRefreshInterval((query.get('inter') || 60) as number);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(() => {
    if (x_api_key && user?.tenant_header) {
      getFarms(x_api_key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, x_api_key]);

  useEffect(() => {
    if (farm?.id && (x_api_key || getToken()) && varieties.length > 0) {
      generateReport({ report_date: new Date() }, { setSubmitting: () => {} });
      const intervalId = setInterval(() => {
        generateReport({ report_date: new Date() }, { setSubmitting: () => {} });
      }, refreshInterval * 60000);

      return () => clearInterval(intervalId);
    }
  }, [varieties]);

  const renderLabelList = (props: any) => {
    const { x, y, width, height, value, index, noKFormat } = props;

    // Custom label styles
    const customStyle = {
      fontSize: '1.2rem',
      fontWeight: '800',
      // textAnchor: 'middle',
      fill: props.dataKey != 'Actuals' ? '#black' : '#white', // white text color
      backgroundColor: '#fff', // black background
      padding: '4px 8px',
      borderRadius: '4px',
      transform: `rotate(-25 ${x} ${y})` // move label slightly above the bar
    };

    return (
      <text x={x + width / 2} y={y + height / 1.8} style={{ ...customStyle, textAnchor: 'middle' }}>
        {value > 0 ? (!noKFormat ? kFormatter(value, 1) : value) : ''}
      </text>
    );
  };
  const renderMultiVarLabelList = (props: any) => {
    const { x, y, width, height, value, index, noKFormat } = props;

    // Custom label styles
    const customStyle = {
      fontSize: '1.2rem',
      fontWeight: '800',
      // textAnchor: 'middle',
      fill: props.dataKey != 'Actuals' ? '#black' : '#white', // white text color
      backgroundColor: '#fff', // black background
      padding: '4px 8px',
      borderRadius: '4px',
      transform: `rotate(-25 ${x} ${y})` // move label slightly above the bar
    };

    return (
      <text x={x + width / 2} y={y + height / 1.8} style={{ ...customStyle, textAnchor: 'middle' }}>
        {value > 0 ? (!noKFormat ? kFormatter(value, 1) : value) : ''}
      </text>
    );
  };

  return (
    <Box
      width="100%"
      boxShadow="0px 1px 4px rgba(12, 12, 13, 0.15)"
      padding={6}
      borderRadius={8}
      height={'100vh'}
      alignItems="center"
      style={{
        background:
          'linear-gradient(90deg, rgba(204, 238, 234, 0.3) 0%, rgba(250, 207, 167, 0.3) 50%,rgba(238, 119, 119, 0.3) 100%)'
      }}
    >
      {/* {!variety || !farm?.id ? ( */}
      {isProcessing || !variety || !farm?.id || isGeneratingReport ? (
        <Box
          backgroundColor={'gray.50'}
          height={'100%'}
          width={'100%'}
          borderRadius={20}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
        >
          <Box height={'50%'}>
            <DotLottieReact
              src="https://lottie.host/8a003f99-36bd-4575-93f3-37888a11b312/qbxGcQaqtz.lottie"
              loop
              autoplay
            />
          </Box>

          <Text color={'primary.400'} textAlign={'center'} size="sm" mt={10} fontSize={'1.6rem'}>
            {/* <Spinner color="primary.500" size="lg" mr={1} /> */}
            Updating live report data. This might take a few minutes
          </Text>

          {/* Progress information */}
          {isGeneratingReport && (
            <>
              <Progress
                hasStripe
                value={progressPercentage}
                width={400}
                size={'md'}
                colorScheme={'teal'}
                mt={6}
                isAnimated={true}
                style={{ borderRadius: 4 }}
              />
              <Text color={'gray.500'} textAlign={'center'} fontSize="md" mt={3}>
                {progressPercentage < 30
                  ? 'Fetching forecast data...'
                  : progressPercentage < 60
                  ? 'Fetching harvest data...'
                  : progressPercentage < 90
                  ? 'Processing data...'
                  : 'Preparing report...'}
              </Text>
              {batchProgress && (
                <Text
                  color={'teal.500'}
                  textAlign={'center'}
                  fontSize="md"
                  mt={2}
                  fontWeight="bold"
                >
                  {batchProgress}
                </Text>
              )}
              {generationTime && (
                <Text
                  color={'teal.600'}
                  textAlign={'center'}
                  fontSize="md"
                  mt={2}
                  fontWeight="bold"
                >
                  Generated in {generationTime}
                </Text>
              )}
            </>
          )}
        </Box>
      ) : (
        <>
          {variety?.id && farm?.id && (
            <>
              {hidePdf ? (
                <Box
                  backgroundColor={'gray.50'}
                  height={'85%'}
                  width={'70%'}
                  borderRadius={8}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Text color={'gray.300'} textAlign={'center'}>
                    Report will be generated here
                  </Text>
                </Box>
              ) : (
                // react component of pdf report
                <Box>
                  <Box
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      marginLeft: '0%',
                      marginRight: '2%'
                    }}
                  >
                    <>
                      {/* Farm Section Summary Pages */}
                      {activePages[activePageIndex].name == 'Farm Section Summary' &&
                        (() => {
                          const currentSectionId = activePages[activePageIndex].sectionId;

                          const sectionData = allFarmSectionData.find(
                            (data) => data.section.id === currentSectionId
                          );

                          if (!sectionData) {
                            return null;
                          }

                          return (
                            <>
                              <Box
                                style={{
                                  display: 'flex',
                                  flexDirection: 'row',
                                  justifyContent: 'space-between'
                                }}
                              >
                                <Box>
                                  <Text
                                    style={{
                                      fontSize: '1.4rem',
                                      marginBottom: 2,
                                      textAlign: 'center',
                                      fontWeight: 500
                                    }}
                                  >
                                    {`${farm?.name} | ${sectionData.section.name}`}
                                  </Text>
                                </Box>
                                <Box style={{ alignItems: 'center' }}>
                                  <Text
                                    style={{
                                      fontSize: '1.4rem',
                                      marginBottom: 2,
                                      textAlign: 'center',
                                      fontWeight: 500
                                    }}
                                  >
                                    Week {reportWeekNo}
                                  </Text>
                                  <Text
                                    style={{
                                      fontSize: '1.1rem',
                                      marginBottom: 2,
                                      textAlign: 'center',
                                      fontWeight: 400
                                    }}
                                  >
                                    {`Date: ${dateString}`}
                                  </Text>
                                </Box>
                                <Box>
                                  <Img style={{ width: '150px' }} src={LogoWithSlogan}></Img>
                                </Box>
                              </Box>
                              <Box
                                style={{
                                  display: 'flex',
                                  flexDirection: 'row',
                                  justifyContent: 'space-between',
                                  marginTop: 0,
                                  marginBottom: 14
                                }}
                              >
                                {/* Total Forecast */}
                                <Box
                                  style={{
                                    ...styles.info_card,
                                    backgroundColor: '#98C9FF',
                                    width: varieties.length === 1 ? '28vw' : styles.info_card.width
                                  }}
                                >
                                  <Box
                                    style={{
                                      ...styles.info_card_top,
                                      backgroundColor: '#98C9FF'
                                    }}
                                  >
                                    <Text style={styles.info_card_value} textAlign="center">
                                      {sectionData.totalForecast.toLocaleString()}
                                    </Text>
                                    <Text style={styles.info_card_description} textAlign="center">
                                      {sectionData.section.name} Forecast
                                    </Text>
                                  </Box>
                                </Box>
                                {/* Active Fields */}
                                <Box
                                  style={{
                                    ...styles.info_card,
                                    backgroundColor: '#89F9C4',
                                    width: varieties.length === 1 ? '28vw' : styles.info_card.width
                                  }}
                                >
                                  <Box style={styles.info_card_top}>
                                    <Text style={styles.info_card_value} textAlign="center">
                                      {sectionData.activeFieldsCount}
                                    </Text>
                                    <Text style={styles.info_card_description} textAlign="center">
                                      Active Fields
                                    </Text>
                                  </Box>
                                </Box>
                              </Box>
                              <Box
                                style={{
                                  display: 'flex',
                                  flexDirection: 'row',
                                  gap: '2vw',
                                  width: '100%',
                                  alignItems: 'flex-start'
                                }}
                              >
                                {/* Graph */}
                                <Box
                                  style={{
                                    alignItems: 'center',
                                    width: '100%',
                                    height: 400,
                                    marginRight: 0
                                  }}
                                  backgroundColor={'#fff'}
                                  borderRadius={12}
                                  padding={12}
                                >
                                  <ResponsiveContainer width={'100%'} height="100%">
                                    <BarChart
                                      data={sectionData.graphData}
                                      margin={{
                                        left: 20,
                                        right: 0,
                                        top: 0,
                                        bottom: 0
                                      }}
                                    >
                                      <CartesianGrid strokeDasharray="3 3" />
                                      <YAxis
                                        fontSize="1.4rem"
                                        type="number"
                                        tickFormatter={(value: string) =>
                                          value && value.toLocaleString()
                                        }
                                      />
                                      <XAxis type="category" dataKey="name" />
                                      <Legend iconType="circle" iconSize={15} />
                                      <Bar
                                        dataKey={'Actuals'}
                                        fill={'#626262'}
                                        isAnimationActive={true}
                                        background={{ fill: '#fff', opacity: 0 }}
                                      >
                                        <LabelList
                                          dataKey={'Actuals'}
                                          position="top"
                                          content={renderLabelList}
                                          style={{ color: '#fff', textAnchor: 'middle' }}
                                        />
                                      </Bar>
                                      <Bar
                                        dataKey={'Forecasts'}
                                        fill={'#00B8FF'}
                                        isAnimationActive={true}
                                        background={{ fill: '#fff', opacity: 0 }}
                                      >
                                        <LabelList
                                          dataKey={'Forecasts'}
                                          position="top"
                                          content={renderLabelList}
                                          style={{ color: '#000', textAnchor: 'middle' }}
                                        />
                                      </Bar>
                                    </BarChart>
                                  </ResponsiveContainer>
                                </Box>
                              </Box>
                            </>
                          );
                        })()}

                      {/* Farm Summary Page */}
                      {activePages[activePageIndex].name == 'Farm Summary' && (
                        <>
                          <Box
                            style={{
                              display: 'flex',
                              flexDirection: 'row',
                              justifyContent: 'space-between'
                            }}
                          >
                            <Box>
                              <Text
                                style={{
                                  fontSize: '1.4rem',
                                  marginBottom: 2,
                                  textAlign: 'center',
                                  fontWeight: 500
                                }}
                              >
                                {`${farm?.name}`}
                              </Text>
                            </Box>
                            <Box style={{ alignItems: 'center' }}>
                              <Text
                                style={{
                                  fontSize: '1.4rem',
                                  marginBottom: 2,
                                  textAlign: 'center',
                                  fontWeight: 500
                                }}
                              >
                                Week {reportWeekNo}
                              </Text>
                              <Text
                                style={{
                                  fontSize: '1.1rem',
                                  marginBottom: 2,
                                  textAlign: 'center',
                                  fontWeight: 400
                                }}
                              >
                                {`Date: ${dateString}`}
                              </Text>
                            </Box>
                            <Box>
                              <Img style={{ width: '150px' }} src={LogoWithSlogan}></Img>
                            </Box>
                          </Box>
                          <Box
                            style={{
                              display: 'flex',
                              flexDirection: 'row',
                              justifyContent: 'space-between',
                              marginTop: 0,
                              marginBottom: 14
                            }}
                          >
                            {/* Total Forecast */}
                            <Box
                              style={{
                                ...styles.info_card,
                                backgroundColor: '#98C9FF',
                                width: varieties.length === 1 ? '28vw' : styles.info_card.width
                              }}
                            >
                              <Box
                                style={{
                                  ...styles.info_card_top,
                                  backgroundColor: '#98C9FF'
                                }}
                              >
                                <Text style={styles.info_card_value} textAlign="center">
                                  {totalFarmForecast.toLocaleString()}
                                </Text>
                                <Text style={styles.info_card_description} textAlign="center">
                                  Total Forecast
                                </Text>
                              </Box>
                            </Box>
                            {/* Harvest Deficit */}
                            <Box
                              style={{
                                ...styles.info_card,
                                backgroundColor:
                                  totalFarmForecast - totalFarmHarvest < 0 ? '#89F9C4' : '#D96C80',

                                width: varieties.length === 1 ? '28vw' : styles.info_card.width
                              }}
                            >
                              <Box style={styles.info_card_top}>
                                <Text style={styles.info_card_value} textAlign="center">
                                  {(totalFarmForecast - totalFarmHarvest).toLocaleString()}
                                </Text>
                                <Text style={styles.info_card_description} textAlign="center">
                                  Harvest Deficit
                                </Text>
                              </Box>
                            </Box>

                            {varieties.length == 1 && (
                              <Box
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  width: '40vw',
                                  height: '25vh',
                                  marginBottom: '2vh',
                                  padding: '1vh 2vw'
                                }}
                              >
                                {/* Upcoming Fields */}
                                <Box
                                  style={{
                                    flexDirection: 'column',
                                    display: 'flex',
                                    flexWrap: 'wrap'
                                  }}
                                >
                                  {varietyGraphData
                                    .slice(-3)
                                    .map((graph_data: any, index: number) => (
                                      <Box
                                        key={index}
                                        style={{
                                          flexDirection: 'row',
                                          display: 'flex',
                                          flexWrap: 'wrap'
                                        }}
                                      >
                                        <Text
                                          style={styles.field_week_item}
                                          textAlign="center"
                                          fontWeight={'800'}
                                        >
                                          {graph_data.name}
                                        </Text>
                                        {graph_data?.forecasted_fields.map(
                                          (field_name: string, f_index: number) => (
                                            <Text
                                              key={f_index}
                                              style={styles.field_grid_item}
                                              textAlign="center"
                                              fontWeight={'800'}
                                            >
                                              {field_name}
                                            </Text>
                                          )
                                        )}
                                        {/* </Box> */}
                                      </Box>
                                    ))}
                                </Box>
                              </Box>
                            )}
                          </Box>
                          <Box
                            style={{
                              display: 'flex',
                              flexDirection: 'row',
                              gap: '2vw',
                              width: '100%',
                              alignItems: 'flex-start'
                            }}
                          >
                            {/* Graph */}
                            <Box
                              style={{
                                alignItems: 'center',
                                width: varieties.length == 1 ? '60%' : '100%',
                                height: 400,
                                marginRight: 0
                              }}
                              backgroundColor={'#fff'}
                              borderRadius={12}
                              padding={12}
                            >
                              <ResponsiveContainer width={'100%'} height="100%">
                                <BarChart
                                  data={varietyGraphData}
                                  margin={{
                                    left: 20,
                                    right: 0,
                                    top: 0,
                                    bottom: 0
                                  }}
                                >
                                  <CartesianGrid strokeDasharray="3 3" />
                                  <YAxis
                                    fontSize="1.4rem"
                                    type="number"
                                    tickFormatter={(value: string) =>
                                      value && value.toLocaleString()
                                    }
                                  />
                                  <XAxis type="category" dataKey="name" />
                                  <Legend iconType="circle" iconSize={15} />
                                  <Bar
                                    dataKey={'Actuals'}
                                    fill={'#626262'}
                                    isAnimationActive={true}
                                    background={{ fill: '#fff', opacity: 0 }}
                                  >
                                    <LabelList
                                      dataKey={'Actuals'}
                                      position="top"
                                      content={renderLabelList}
                                      style={{ color: '#fff', textAnchor: 'middle' }}
                                    />
                                  </Bar>
                                  <Bar
                                    dataKey={'Forecasts'}
                                    fill={'#00B8FF'}
                                    isAnimationActive={true}
                                    background={{ fill: '#fff', opacity: 0 }}
                                  >
                                    <LabelList
                                      dataKey={'Forecasts'}
                                      position="top"
                                      content={renderLabelList}
                                      style={{ color: '#000', textAnchor: 'middle' }}
                                    />
                                  </Bar>
                                </BarChart>
                              </ResponsiveContainer>
                            </Box>
                            {/* Production Category Table */}
                            {varieties.length == 1 && varietyGraphData.length > 0 && (
                              <Box
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  width: '35vw',
                                  height: '400px',
                                  marginBottom: '2vh',
                                  padding: '1vh 2vw',
                                  backgroundColor: '#fff',
                                  borderRadius: 12
                                }}
                              >
                                {/* Create a table showing the forecast per production category from the varietyForecast */}
                                {/* create columns with first one being names that will have the production categories then the rest will be different weeks */}
                                <TableContainer style={{ width: '100%' }}>
                                  <Table
                                    variant="simple"
                                    size="sm"
                                    style={{
                                      fontSize: '1rem',
                                      fontFamily: 'Be Vietnam Pro'
                                    }}
                                  >
                                    <Thead>
                                      <Tr
                                        style={{
                                          backgroundColor: 'rgba(245, 245, 245, 1)',
                                          borderBottom: '0.5px solid rgba(0, 0, 0, 0.06)'
                                        }}
                                      >
                                        <Th></Th>
                                        {varietyGraphData?.map((graph_data: any, index: number) => (
                                          <Th key={index} isNumeric style={{ color: '#000000' }}>
                                            {graph_data.name}
                                          </Th>
                                        ))}
                                      </Tr>
                                    </Thead>
                                    <Tbody>
                                      <Tr
                                        style={{
                                          backgroundColor: 'background: rgba(128, 128, 128, 0.2)',
                                          borderBottom: '0.5px solid rgba(0, 0, 0, 0.06)'
                                        }}
                                      >
                                        <Td style={{ fontWeight: '600' }}>Actual</Td>
                                        {varietyGraphData?.map(
                                          (graph_data: any, week_index: number) => (
                                            <Td key={week_index} isNumeric>
                                              {graph_data.Actuals.toLocaleString()}
                                            </Td>
                                          )
                                        )}
                                      </Tr>
                                      <Tr
                                        style={{
                                          backgroundColor: 'rgba(83, 165, 255, 0.2)',
                                          borderBottom: '0.5px solid rgba(0, 0, 0, 0.06)'
                                        }}
                                      >
                                        <Td style={{ fontWeight: '600' }}>Forecast</Td>
                                        {varietyGraphData?.map(
                                          (graph_data: any, week_index: number) => (
                                            <Td key={week_index} isNumeric>
                                              {graph_data.Forecasts.toLocaleString()}
                                            </Td>
                                          )
                                        )}
                                      </Tr>
                                      {varieties[0]?.production_categories &&
                                        varietyForecasts[0] &&
                                        varieties[0]?.production_categories.map(
                                          (
                                            production_category: ProductionCategory,
                                            prod_cat_index: number
                                          ) => (
                                            <Tr
                                              key={prod_cat_index}
                                              style={{
                                                borderBottom: '0.5px solid rgba(0, 0, 0, 0.06)'
                                              }}
                                            >
                                              <Td>{production_category.name}</Td>
                                              {varietyGraphData?.map(
                                                (graph_data: any, week_index: number) => (
                                                  <Td key={week_index} isNumeric>
                                                    {Number.isFinite(
                                                      Number(
                                                        varietyForecasts[0]?.detailed_view[
                                                          week_index
                                                        ][prod_cat_index]
                                                      )
                                                    )
                                                      ? Number(
                                                          varietyForecasts[0]?.detailed_view[
                                                            week_index
                                                          ][prod_cat_index]
                                                        ).toLocaleString()
                                                      : ''}
                                                  </Td>
                                                )
                                              )}
                                            </Tr>
                                          )
                                        )}
                                    </Tbody>
                                  </Table>
                                </TableContainer>
                              </Box>
                            )}
                          </Box>
                        </>
                      )}

                      {/* Multiple Varieties Page */}
                      {activePages[activePageIndex].name == 'Multiple-Varieties' &&
                        (() => {
                          // Calculate which varieties to show on this page
                          const currentVarietyPageIndex =
                            activePages[activePageIndex].varietyPageIndex || 0;
                          const startIndex = currentVarietyPageIndex * 15;
                          const endIndex = Math.min(
                            startIndex + 15,
                            individualVarietyGraphData.length
                          );
                          // Pair graph data with metadata, sort by color name then variety name
                          const pairedData = individualVarietyGraphData
                            .map((graphData: any, i: number) => ({
                              graphData,
                              metadata: varieties[i]
                            }))
                            .sort((a: any, b: any) => {
                              const colorA = a.metadata?.colors?.[0]?.name?.toLowerCase() || '';
                              const colorB = b.metadata?.colors?.[0]?.name?.toLowerCase() || '';
                              if (colorA !== colorB) return colorA.localeCompare(colorB);
                              const shadeA = a.metadata?.colors?.[0]?.shade?.toLowerCase() || '';
                              const shadeB = b.metadata?.colors?.[0]?.shade?.toLowerCase() || '';
                              if (shadeA !== shadeB) return shadeA.localeCompare(shadeB);
                              const nameA = a.metadata?.name?.toLowerCase() || '';
                              const nameB = b.metadata?.name?.toLowerCase() || '';
                              return nameA.localeCompare(nameB);
                            });

                          const varietiesForThisPage = pairedData
                            .slice(startIndex, endIndex)
                            .map((p: any) => p.graphData);
                          const varietiesMetadataForThisPage = pairedData
                            .slice(startIndex, endIndex)
                            .map((p: any) => p.metadata);

                          // Calculate the number of rows actually needed for varieties on this page
                          const actualRowsNeeded = Math.ceil(
                            varietiesForThisPage.length / varietyGridConfig.items_per_row
                          );

                          return (
                            <Box
                              style={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                                marginTop: 0,
                                marginBottom: 0,
                                height: '85vh',
                                width: '100%'
                              }}
                            >
                              <Box
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  justifyContent: 'space-between',
                                  flexWrap: 'wrap',
                                  width: '8%',
                                  height: '95%',
                                  marginTop: '3%'
                                }}
                              >
                                {/* Only show week labels for rows that have variety items */}
                                {Array.from({ length: actualRowsNeeded }).map((_, index) => (
                                  <Box
                                    key={index}
                                    style={{
                                      marginTop: '1%',
                                      alignItems: 'center',
                                      width: '100%',
                                      height: varietyGridConfig.item_height
                                    }}
                                  >
                                    <ResponsiveContainer
                                      width={'100%'}
                                      height="80%"
                                      style={{ marginTop: '16%' }}
                                    >
                                      <BarChart
                                        // width={1350}
                                        // height={300}
                                        data={varietyGraphData}
                                        layout="vertical"
                                        margin={{
                                          left: 10,
                                          right: 0,
                                          top: 0,
                                          bottom: 0
                                        }} // Increased left and right margins
                                      >
                                        <YAxis
                                          type="category"
                                          dataKey="name"
                                          fontSize="0.9rem"
                                          tickCount={5}
                                          axisLine={false}
                                        />
                                      </BarChart>
                                    </ResponsiveContainer>
                                  </Box>
                                ))}
                              </Box>
                              <Box
                                style={{
                                  display: 'flex',
                                  flexDirection: 'row',
                                  flexWrap: 'wrap',
                                  justifyContent: 'flex-start',
                                  columnGap: '2%',
                                  rowGap: '1%',
                                  width: '100%',
                                  height: '100%',
                                  overflow: 'visible'
                                }}
                              >
                                {varietiesForThisPage.map((graph_data: any, index: number) => (
                                  <Box
                                    key={startIndex + index}
                                    style={{
                                      marginTop: '1%',
                                      alignItems: 'center',
                                      width: varietyGridConfig.item_width,
                                      height: varietyGridConfig.item_height
                                    }}
                                    backgroundColor={'#fff'}
                                    borderRadius={6}
                                    paddingLeft={4}
                                    paddingRight={4}
                                  >
                                    <Text
                                      style={{
                                        fontSize: '1.1rem',
                                        marginBottom: 0,
                                        marginTop: 6,
                                        fontFamily: 'Be Vietnam Pro',
                                        fontWeight: 700
                                      }}
                                    >
                                      {varietiesMetadataForThisPage[index].name}
                                    </Text>
                                    {varietiesMetadataForThisPage[index]?.colors?.[0] && (
                                      <Text
                                        style={{
                                          fontSize: '0.7rem',
                                          marginBottom: 2,
                                          fontFamily: 'Be Vietnam Pro',
                                          fontWeight: 400,
                                          color: '#666'
                                        }}
                                      >
                                        {varietiesMetadataForThisPage[index].colors[0].name}
                                        {varietiesMetadataForThisPage[index].colors[0].shade
                                          ? ` - ${varietiesMetadataForThisPage[index].colors[0].shade}`
                                          : ''}
                                      </Text>
                                    )}
                                    <ResponsiveContainer width={'100%'} height="85%">
                                      <BarChart
                                        // width={1350}
                                        // height={300}
                                        data={graph_data}
                                        layout="vertical"
                                        margin={{
                                          left: -50,
                                          right: 55,
                                          top: 0,
                                          bottom: 0
                                        }} // Increased left and right margins
                                      >
                                        <YAxis
                                          type="category"
                                          dataKey="name"
                                          fontSize="0.9rem"
                                          //only include ticks for first graph per row
                                          tick={false}
                                          tickCount={5}
                                        />
                                        <XAxis
                                          fontSize="0.9rem"
                                          type="number"
                                          tick={false}
                                          axisLine={false}
                                        />
                                        {/* <Legend iconType="circle" iconSize={15} /> */}
                                        <Bar
                                          dataKey={'Forecasts'}
                                          fill={
                                            varietiesMetadataForThisPage[index]?.colors[0]
                                              .hex_code || graphColors[index]
                                          }
                                          isAnimationActive={true}
                                          background={{ fill: '#fff', opacity: 0 }}
                                          barSize={12}
                                          stroke={'#666'}
                                          strokeWidth={0.5}
                                        >
                                          <LabelList
                                            dataKey={'Forecasts'}
                                            position="right"
                                            content={(props: any) => {
                                              const { x, y, width, height, value } = props;
                                              return (
                                                <text
                                                  x={x + width + 8}
                                                  y={y + height / 2 + 4}
                                                  style={{
                                                    fill: '#000',
                                                    fontWeight: 700,
                                                    fontSize: '1rem',
                                                    textAnchor: 'start'
                                                  }}
                                                >
                                                  {value > 0 ? kFormatter(value, 1) : ''}
                                                </text>
                                              );
                                            }}
                                          />
                                        </Bar>
                                        <Bar
                                          dataKey={'Actuals'}
                                          fill={'#000'}
                                          isAnimationActive={true}
                                          background={{ fill: '#fff', opacity: 0 }}
                                          barSize={3}
                                        ></Bar>
                                      </BarChart>
                                    </ResponsiveContainer>
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          );
                        })()}
                    </>
                  </Box>
                  <Text
                    style={{
                      fontSize: '0.9rem',
                      marginTop: 20,
                      marginBottom: 2,
                      fontWeight: 500
                    }}
                  >
                    {activePages[activePageIndex].name === 'Multiple-Varieties'
                      ? (() => {
                          const currentVarietyPageIndex =
                            activePages[activePageIndex].varietyPageIndex || 0;
                          const startIndex = currentVarietyPageIndex * 15 + 1;
                          const endIndex = Math.min(
                            (currentVarietyPageIndex + 1) * 15,
                            varieties.length
                          );
                          const totalPages = Math.ceil(varieties.length / 15);
                          return `Varieties (${startIndex}-${endIndex} of ${
                            varieties.length
                          }) - Page ${currentVarietyPageIndex + 1}/${totalPages}`;
                        })()
                      : (() => {
                          return ``;
                        })()}
                  </Text>
                  <Text style={{ fontSize: '0.6rem' }}>
                    {farm?.name + ' ' + farm?.city.replaceAll('_', ' ') + ' '} , {farm?.country} |{' '}
                    {activePages[activePageIndex].name === 'Multiple-Varieties'
                      ? (() => {
                          return `Multiple-Varieties`;
                        })()
                      : activePages[activePageIndex].name === 'Farm Section Summary'
                      ? (() => {
                          const currentSectionId = activePages[activePageIndex].sectionId;
                          const sectionData = allFarmSectionData.find(
                            (data) => data.section.id === currentSectionId
                          );
                          return sectionData
                            ? `${sectionData.section.name} Summary`
                            : 'Farm Section Summary';
                        })()
                      : activePages[activePageIndex].name + ' page '}{' '}
                    | Last Updated at {lastUpdated}
                  </Text>
                </Box>
              )}
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default LiveReporting;
